import { useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUpRight, Globe, Mail, MessageCircle, Send, Shuffle, Users, Wallet } from 'lucide-react';
import { MarketingLayout } from '@/components/marketing/MarketingLayout';
import { PageHero, Reveal } from '@/components/marketing/primitives';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  SALES_ENQUIRY_TYPES,
  isSalesEnquiryType,
  salesContactSchema,
  salesEnquirySubject,
  salesEnquiryTypeLabel,
  type SalesContactValues,
  type SalesEnquiryType,
} from '@/lib/enquiry-types';
import '@/styles/marketing-pages.css';

/** Common questions. Picking one fills in "What's it about?" on the form. */
const topics: { icon: typeof Wallet; label: string; type: SalesEnquiryType }[] = [
  { icon: Wallet, label: 'Which plan fits my business?', type: 'pricing' },
  { icon: Globe, label: 'Connecting my own domain', type: 'setup' },
  { icon: Shuffle, label: 'Moving from my current website', type: 'switching' },
  { icon: Users, label: 'Setting up my team', type: 'setup' },
];

const SalesContact = () => {
  const [searchParams] = useSearchParams();
  // /sales-contact?topic=partner (or demo, pricing…) opens with the type chosen.
  const topicParam = searchParams.get('topic');
  const form = useForm<SalesContactValues>({
    resolver: zodResolver(salesContactSchema),
    defaultValues: {
      name: '',
      email: '',
      business: '',
      message: '',
      ...(isSalesEnquiryType(topicParam) ? { enquiryType: topicParam } : {}),
    },
  });
  const typeTriggerRef = useRef<HTMLButtonElement | null>(null);

  const pickTopic = (type: SalesEnquiryType) => {
    form.setValue('enquiryType', type, { shouldValidate: true });
    typeTriggerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    typeTriggerRef.current?.focus({ preventScroll: true });
  };

  const handleSubmit = (values: SalesContactValues) => {
    const subject = encodeURIComponent(salesEnquirySubject(values.enquiryType, values.name));
    const body = encodeURIComponent(
      `About: ${salesEnquiryTypeLabel(values.enquiryType)}\nName: ${values.name}\nEmail: ${values.email}\nBusiness: ${values.business ?? ''}\n\n${values.message}`
    );
    window.location.href = `mailto:hello@thepolishedversion.com?subject=${subject}&body=${body}`;
  };

  return (
    <MarketingLayout>
      <PageHero
        eyebrow={
          <>
            <MessageCircle className="h-3.5 w-3.5 text-[hsl(var(--mk-sun))]" /> Talk to us
          </>
        }
        title={
          <>
            Got questions? <span className="mk-mark mk-mark-block text-[hsl(var(--mk-ink))]">Let’s talk.</span>
          </>
        }
        lede="Plans, domains, moving from your old site or setting up your team. Ask away. No sales script, just straight answers."
      />

      <section className="relative z-10 -mt-12 pb-28 md:-mt-14 md:pb-36">
        <div className="container mx-auto grid max-w-6xl px-4 gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
          {/* Form */}
          <Reveal className="mk-card p-7 sm:p-10">
            <h2 className="font-display text-3xl leading-tight">Send us a message</h2>
            <p className="mt-2 text-[hsl(var(--mk-muted))]">Fill this in and it opens straight in your email app, ready to send.</p>
            <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-8 space-y-5" noValidate>
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="font-semibold">Name</FormLabel>
                      <FormControl>
                        <Input className="mk-field" placeholder="Your name" autoComplete="name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="font-semibold">Email</FormLabel>
                      <FormControl>
                        <Input className="mk-field" type="email" placeholder="you@example.com" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="business"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-semibold">
                      Business name <span className="font-normal text-[hsl(var(--mk-muted))]">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input className="mk-field" placeholder="Your cleaning business" autoComplete="organization" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="enquiryType"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-semibold">What's it about?</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger
                          ref={(el) => {
                            typeTriggerRef.current = el;
                            field.ref(el);
                          }}
                          onBlur={field.onBlur}
                          className="mk-field"
                        >
                          <SelectValue placeholder="Choose a topic" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SALES_ENQUIRY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="font-semibold">Message</FormLabel>
                    <FormControl>
                      <Textarea className="mk-field" placeholder="What would you like to know?" rows={6} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <button type="submit" className="mk-btn mk-btn-sun w-full">
                <Send className="h-4 w-4" /> Send message
              </button>
            </form>
            </Form>
          </Reveal>

          {/* Side */}
          <div className="space-y-5">
            <Reveal delay={100} className="mk-ink-bg mk-on-dark rounded-[2rem] p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--mk-mint))] text-[hsl(var(--mk-ink))]">
                <Mail className="h-5 w-5" />
              </span>
              <h2 className="mt-6 font-display text-2xl text-white">Rather just email?</h2>
              <a
                href="mailto:hello@thepolishedversion.com"
                className="mt-2 inline-block break-words font-display text-base font-semibold sm:text-lg text-[hsl(var(--mk-sun))] hover:underline"
              >
                hello@thepolishedversion.com
              </a>
            </Reveal>

            <Reveal delay={160} className="mk-panel rounded-[2rem] p-8">
              <h2 className="font-display text-xl">Owners usually ask us about</h2>
              <p className="mt-1 text-sm text-[hsl(var(--mk-muted))]">Tap one and we'll pick the topic for you.</p>
              <ul className="mt-5 space-y-3">
                {topics.map((t) => (
                  <li key={t.label}>
                    <button
                      type="button"
                      onClick={() => pickTopic(t.type)}
                      className="group flex w-full items-center gap-3 rounded-xl text-left font-medium transition-colors hover:text-[hsl(var(--mk-violet))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--mk-violet))] focus-visible:ring-offset-2"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[hsl(var(--mk-violet))]">
                        <t.icon className="h-4 w-4" />
                      </span>
                      <span className="flex-1">{t.label}</span>
                      <span className="sr-only">: ask us about this</span>
                    </button>
                  </li>
                ))}
              </ul>
              <Link
                to="/faq"
                className="group mt-7 flex items-center justify-between rounded-2xl bg-white px-5 py-4 font-display font-semibold transition-colors hover:bg-[hsl(var(--mk-ink))] hover:text-white"
              >
                Find quick answers in the FAQ
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </Reveal>

            <Reveal delay={220} className="rounded-[2rem] border border-[hsl(var(--border))] p-8">
              <h2 className="font-display text-xl">Want to see it first?</h2>
              <p className="mt-2 text-[hsl(var(--mk-muted))]">Click around a demo site, or start your free trial and see it from the inside.</p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3 font-display font-semibold">
                <Link to="/demo" className="flex items-center gap-1.5 text-[hsl(var(--mk-violet))] hover:underline">
                  View the live demo <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/signup" className="flex items-center gap-1.5 text-[hsl(var(--mk-violet))] hover:underline">
                  Start free trial <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default SalesContact;
